import Head from 'next/head';
import { Users, Ruler, CalendarClock, Hammer } from "lucide-react";
import Header from '../components/Header';
import Split from '../components/Split';
import Hero from '../components/Hero';
import Services from '../components/Services';
import Gallery from '../components/Gallery';
import Testimonials from '../components/Testimonials';
import Videos from '../components/Videos';
import FAQ from "../components/FAQ";
import SocialProof from '../components/SocialProof';
import ContactUs from '../components/ContactUs';
import Footer from '../components/Footer';
import QuickQuoteForm from '../components/QuickQuoteForm';
import ContentBlock from '../components/ContentBlock';
import Benefits from '../components/Benefits';
import { getImages } from '../utils/getImages'; // Import the utility function
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function Home({ galleryImages }) {
  return (
     <div className="bg-white">
    <>
      <Head>
        <title>Home Remodeling Experts | Free Estimate | Modern Build</title>
        <meta name="title" content="Home Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="description" content="Expert home remodeling in Southern California. Kitchens, baths, ADUs, and full-home projects. Get your free estimate today - (323) 300-4130" />
        <meta name="keywords" content="home remodeling, kitchen remodeling, bathroom remodeling, adu convertion, garge convertion, roofing, energy-efficient services, Los Angeles, califronia" />
        <meta name="author" content="Modern Build Home Remodeling" />

        {/* Canonical URL to avoid duplicate content issues */}
        <link rel="canonical" href="https://modernbuildremodel.com/" />

        {/* Open Graph Meta Tags for social sharing */}
        <meta property="og:title" content="Home Remodeling Experts | Free Estimate | Modern Build" />
        <meta property="og:description" content="Expert home remodeling in Southern California. Kitchens, baths, ADUs, and full-home projects. Get your free estimate today - (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Home Remodeling Experts | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Expert home remodeling in Southern California. Kitchens, baths, ADUs, and full-home projects. Get your free estimate today - (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />
       
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "@id": "https://modernbuildremodel.com#webpage",
              "name": "Modern Build Home Remodeling | Los Angeles, CA",
              "url": "https://modernbuildremodel.com",
              "description": "Transform your home with expert remodeling services from Modern Build in Los Angeles and across Southern California.",
              "inLanguage": "en-US",
              "publisher": { "@id": "https://modernbuildremodel.com#localbusiness" },
              "mainEntity": { "@id": "https://modernbuildremodel.com#localbusiness" },
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
              ]
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "@id": "https://modernbuildremodel.com/#website",
              "url": "https://modernbuildremodel.com",
              "name": "Modern Build Home Remodeling",
              "publisher": { "@id": "https://modernbuildremodel.com/#localbusiness" }
            }),
          }}
        />


      </Head>
      <Header />
      <Hero category="home" page="home" hasContentH1={false}/>
      <QuickQuoteForm page="home"/>
      <Split
        subtitle="Modern Build"
        title="High-End Home Remodeling Services in Los Angeles"
        background="white"
        description="Transform your home with our expert remodeling services. From kitchens and bathrooms to full home renovations, we deliver quality craftsmanship and timeless design."
        extraText="Serving Los Angeles and surrounding areas with decades of experience, our team brings precision and elegance to every project. Let’s build your vision together."
        phone="(323) 300-4130"
        image1="/split/kitchen-remodeling-in-los-angeles-ca.webp"
        image2="/split/home-remodeling-in-los-angeles-ca.webp"
        image1="/split/best-complete-home-remodeling-in-los-angeles-ca.webp"
        image2="/split/bathroom-remodeling-in-los-angeles-ca.webp"
        reverse={false}
      />
      <Benefits
        title="Why Choose Modern Build"
        subtitle="Luxury Home Remodeling in Los Angeles"
        items={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Experienced Team",
            description: "Our licensed and insured team delivers luxury kitchen remodels, bathroom renovations, ADU construction, and full home renovations across Los Angeles with end-to-end project management."
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Custom Designs",
            description: "Layouts, finishes, and upgrades are tailored to your lifestyle, goals, and budget. Every remodel improves functionality, aesthetics, and property value."
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Transparent Planning",
            description: "We provide detailed project plans, clear timelines, and upfront cost breakdowns so your home remodeling in Los Angeles stays stress-free and predictable."
          },
          {
            icon: <Hammer className="w-10 h-10 text-primary" />,
            title: "Expert Craftsmanship",
            description: "From high-end kitchen renovations to full home remodels and ADU projects, we focus on premium materials, expert craftsmanship, and results built to last."
          }
        ]}
      />
      <ContentBlock
        subtitle="Luxury Renovations in Los Angeles, Made Simple"
        title="Transform Your Home with Modern Build"
        description="Modern Build delivers high-end kitchen and bathroom remodels, ADU construction, and complete home renovations in Los Angeles, CA. Every project is carefully planned, executed, and inspected for lasting beauty, function, and value."
        extraText="Our licensed and insured team handles every step, from design consultation and permits to construction and final walkthrough. We make home remodeling stress-free, efficient, and fully tailored to your goals, timeline, and budget."
        buttonText="Get Your Free Estimate"
        background="white"
      />

      <Services category="home" />
      <Videos />
      <Testimonials category="home"/>
      <Gallery images={galleryImages} />

      <FAQ category="home"/>
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}

// Use the getImages function to fetch the images in getServerSideProps
export async function getStaticProps() {
  const galleryImages = getImages('home');

  return {
    props: {
      galleryImages,
    },
    revalidate: 60, // Regenerate every 60 seconds (adjust as needed)
  };
}
